import { Badge, Button, cn } from '@design-system';
import { TrialVideoDialog } from '@/features/trial-mode';

import { Check, Clock } from 'lucide-react';
import { NylPilotClientCreationRestricted } from '@/shared/components/nyl-pilot-client-creation-restricted';

export interface TrialStep {
  stepNumber: number;
  title: string;
  description: string;
  hint?: string;
  icon: React.ReactNode;
  isComplete: boolean;
  isCurrent: boolean;
  onAction?: () => void;
  actionLabel?: string;
  isClientCreationAction?: boolean;
  videoUrl?: string;
}

export function TrialStepItem({
  stepNumber,
  title,
  description,
  hint,
  icon,
  isComplete,
  isCurrent,
  onAction,
  actionLabel = 'Start',
  isClientCreationAction = false,
  videoUrl,
}: TrialStep) {
  const actionButton = onAction ? <Button onClick={onAction}>{actionLabel}</Button> : null;

  return (
    <div
      className={cn('flex items-start gap-4 p-4', {
        'bg-primary/5': isCurrent,
        'select-none opacity-70': isComplete,
      })}
    >
      <div
        className={cn('flex size-9 shrink-0 items-center justify-center rounded-full', {
          'bg-green-600 text-white': isComplete,
          'bg-primary text-primary-foreground': isCurrent && !isComplete,
          'bg-accent text-accent-foreground': !isComplete && !isCurrent,
        })}
      >
        {isComplete ? <Check className="size-4" /> : icon}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-foreground text-sm">Step {stepNumber}</span>
          {isCurrent && <Badge>Current</Badge>}
          {isComplete && <Badge variant="success">Completed</Badge>}
        </div>
        <h4 className="font-semibold text-gray-950">{title}</h4>
        <p className="text-foreground text-sm">{description}</p>
        {hint && !isComplete && (
          <p className="text-muted-foreground mt-1 flex items-center gap-1 text-sm">
            <Clock className="size-3.5" />
            {hint}
          </p>
        )}
      </div>

      {isCurrent && (
        <div className="flex flex-col gap-2">
          {isClientCreationAction && actionButton ? (
            <NylPilotClientCreationRestricted side="left">
              {actionButton}
            </NylPilotClientCreationRestricted>
          ) : (
            actionButton
          )}
          {videoUrl && <TrialVideoDialog videoUrl={videoUrl} />}
        </div>
      )}
    </div>
  );
}
